<script lang="ts">
  import { onMount } from 'svelte';
  import { appStore } from '../stores/appStore';
  import { getTokenStorage } from '../utils/localStorage';
  import { AppApiClient } from '../api_contracts/app.client';
  import Navbar from '../components/Navbar.svelte';
  import { UserProfile } from '../api_contracts/app';

  // State
  let client: AppApiClient | null = $state(null);
  let profile: UserProfile | null = $state(null);
  let token: string = $state('');
  let repoName = $state('');
  let ownerId = $state('');

  // Branch data
  interface Branch {
    name: string;
    commitSha: string;
    commitMessage: string;
    commitAuthor: string;
    commitDate: string;
    isDefault: boolean;
    isProtected: boolean;
  }

  let branches: Branch[] = $state([]);
  let defaultBranch = $state('');
  let loading = $state(true);
  let error = $state('');

  // Create branch modal
  let showCreateModal = $state(false);
  let newBranchName = $state('');
  let sourceBranch = $state('');
  let creating = $state(false);
  let createError = $state('');

  // Delete confirmation
  let branchToDelete: string | null = $state(null);
  let deleting = $state(false);

  // Subscribe to store
  appStore.subscribe((state) => {
    profile = state.profile;
    client = state.client;
    token = state.token;
  });

  async function fetchUserProfile() {
    if (!client || !token) {
      error = 'Not authenticated';
      loading = false;
      return false;
    }

    try {
      const result = await client.getUserProfile({ token });

      if (result.response.response.successFull) {
        profile = result.response.profile;
        ownerId = profile.id;
        return true;
      } else {
        error = result.response.response.reason || 'Failed to fetch user profile.';
        return false;
      }
    } catch (e: any) {
      error = e.message || 'Error fetching user profile';
      console.error('Error fetching user profile:', e);
      return false;
    }
  }

  async function fetchBranches() {
    if (!client || !token) {
      console.log('Missing client or token');
      return;
    }

    // Use profile.id directly if profile exists, otherwise use ownerId
    const ownerIdToUse = profile?.id || ownerId;
    if (!ownerIdToUse) {
      console.log('Missing owner ID');
      loading = false;
      return;
    }

    try {
      loading = true;
      error = '';

      console.log('Fetching branches for:', repoName, 'owner:', ownerIdToUse);

      const result = await client.getBranches({
        token,
        ownerId: ownerIdToUse,
        repositoryName: repoName,
      });

      console.log('getBranches result:', result);

      if (result.response.successFull) {
        branches = (result.response.branches || []).map((b: any) => ({
          name: b.name,
          commitSha: b.commitSha,
          commitMessage: b.commitMessage,
          commitAuthor: b.commitAuthor,
          commitDate: formatDate(b.commitDate),
          isDefault: b.isDefault,
          isProtected: b.isProtected,
        }));

        defaultBranch = result.response.defaultBranch || 'main';
      } else {
        error = result.response.reason || 'Failed to fetch branches';
        console.error('Failed to fetch branches:', result.response.reason);
      }
    } catch (e: any) {
      error = e.message || 'Error fetching branches';
      console.error('Error fetching branches:', e);
    } finally {
      loading = false;
    }
  }

  function formatDate(timestamp: any): string {
    if (!timestamp) return 'unknown';
    try {
      const date = new Date(timestamp.seconds * 1000);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    } catch {
      return 'unknown';
    }
  }

  function openCreateModal() {
    newBranchName = '';
    sourceBranch = defaultBranch;
    createError = '';
    showCreateModal = true;
  }

  async function createBranch() {
    if (!client || !token || !ownerId) {
      return;
    }

    if (!newBranchName.trim()) {
      createError = 'Branch name is required';
      return;
    }

    try {
      creating = true;
      createError = '';

      const result = await client.createBranch({
        token,
        ownerId,
        repositoryName: repoName,
        branchName: newBranchName.trim(),
        sourceBranch: sourceBranch,
      });

      if (result.response.successFull) {
        showCreateModal = false;
        fetchBranches(); // Refresh the list
      } else {
        createError = result.response.reason || 'Failed to create branch';
      }
    } catch (e: any) {
      createError = e.message || 'Error creating branch';
      console.error('Error creating branch:', e);
    } finally {
      creating = false;
    }
  }

  async function deleteBranch(branchName: string) {
    if (!client || !token || !ownerId) {
      return;
    }

    try {
      deleting = true;

      const result = await client.deleteBranch({
        token,
        ownerId,
        repositoryName: repoName,
        branchName,
      });

      if (result.response.successFull) {
        branchToDelete = null;
        fetchBranches(); // Refresh the list
      } else {
        error = result.response.reason || 'Failed to delete branch';
      }
    } catch (e: any) {
      error = e.message || 'Error deleting branch';
      console.error('Error deleting branch:', e);
    } finally {
      deleting = false;
    }
  }

  async function setDefaultBranch(branchName: string) {
    if (!client || !token || !ownerId) {
      return;
    }

    try {
      const result = await client.setDefaultBranch({
        token,
        ownerId,
        repositoryName: repoName,
        branchName,
      });

      if (result.response.successFull) {
        fetchBranches(); // Refresh the list
      } else {
        error = result.response.reason || 'Failed to set default branch';
      }
    } catch (e: any) {
      error = e.message || 'Error setting default branch';
      console.error('Error setting default branch:', e);
    }
  }

  onMount(async () => {
    // Extract username and repo name from hash: /users/:username/repositories/:name/branches
    const hash = window.location.hash;
    const match = hash.match(/\/users\/([^\/]+)\/repositories\/([^\/]+)\/branches/);
    if (match) {
      ownerId = match[1]; // This is actually the username
      repoName = match[2];
    }

    console.log('Branches page mounted. Repo:', repoName);

    // Check if authenticated
    const storedToken = getTokenStorage();
    if (!storedToken) {
      error = 'Please login to view branches';
      loading = false;
      return;
    }

    // Wait a bit for store to initialize
    await new Promise(resolve => setTimeout(resolve, 100));

    if (!profile) {
      console.log('Profile not in store, fetching...');
      const success = await fetchUserProfile();
      if (success) {
        console.log('Profile fetched successfully, now fetching branches');
        fetchBranches();
      } else {
        console.error('Failed to fetch profile');
        loading = false;
      }
    } else {
      console.log('Profile exists in store:', profile.id);
      ownerId = profile.id;
      fetchBranches();
    }
  });
</script>

<Navbar />

<div class="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
  <div class="container mx-auto px-4 py-8 max-w-7xl">
    <!-- Header -->
    <div class="mb-6 flex items-center justify-between">
      <div>
        <a
          href="#/users/{ownerId}/repositories/{repoName}"
          class="text-blue-600 dark:text-blue-400 hover:underline mb-2 inline-block"
        >
          ← Back to repository
        </a>
        <h1 class="text-3xl font-bold">Branches</h1>
        <p class="text-gray-600 dark:text-gray-400 mt-1">
          Manage branches for {repoName}
        </p>
      </div>
      <button
        onclick={openCreateModal}
        class="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
        New branch
      </button>
    </div>

    {#if loading}
      <div class="flex items-center justify-center min-h-[400px]">
        <div class="text-center">
          <div class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p class="mt-4 text-gray-600 dark:text-gray-400">Loading branches...</p>
        </div>
      </div>
    {:else if error}
      <div class="p-8 text-center bg-red-50 dark:bg-red-900/20 rounded-lg">
        <svg class="w-16 h-16 mx-auto text-red-500 mb-4" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
        </svg>
        <p class="text-red-600 dark:text-red-400 text-lg font-semibold">{error}</p>
      </div>
    {:else}
      <!-- Default Branch -->
      <div class="mb-6">
        <h2 class="text-xl font-semibold mb-3 flex items-center gap-2">
          <svg class="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
          </svg>
          Default Branch
        </h2>
        {#each branches.filter(b => b.isDefault) as branch}
          <div class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div class="flex items-center justify-between">
              <div class="flex-1">
                <div class="flex items-center gap-3">
                  <h3 class="text-lg font-semibold">{branch.name}</h3>
                  <span class="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 text-xs font-medium rounded">
                    Default
                  </span>
                  {#if branch.isProtected}
                    <span class="px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400 text-xs font-medium rounded">
                      Protected
                    </span>
                  {/if}
                </div>
                <div class="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  <p class="truncate">{branch.commitMessage}</p>
                  <p class="mt-1">
                    {branch.commitAuthor} committed {branch.commitSha.substring(0, 7)} on {branch.commitDate}
                  </p>
                </div>
              </div>
            </div>
          </div>
        {/each}
      </div>

      <!-- Other Branches -->
      <div>
        <h2 class="text-xl font-semibold mb-3">
          All Branches ({branches.length})
        </h2>
        <div class="space-y-2">
          {#each branches as branch}
            <div class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-gray-300 dark:hover:border-gray-600 transition-colors">
              <div class="flex items-center justify-between">
                <div class="flex-1">
                  <div class="flex items-center gap-3">
                    <svg class="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                    </svg>
                    <h3 class="font-medium">{branch.name}</h3>
                    {#if branch.isDefault}
                      <span class="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 text-xs font-medium rounded">
                        Default
                      </span>
                    {/if}
                    {#if branch.isProtected}
                      <span class="px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400 text-xs font-medium rounded">
                        Protected
                      </span>
                    {/if}
                  </div>
                  <div class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    <p class="truncate">{branch.commitMessage}</p>
                    <p class="mt-1">
                      {branch.commitAuthor} · {branch.commitSha.substring(0, 7)} · {branch.commitDate}
                    </p>
                  </div>
                </div>
                <div class="flex items-center gap-2">
                  {#if !branch.isDefault}
                    <button
                      onclick={() => setDefaultBranch(branch.name)}
                      class="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                      title="Set as default"
                    >
                      Set default
                    </button>
                    <button
                      onclick={() => branchToDelete = branch.name}
                      class="px-3 py-1 text-sm bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 rounded transition-colors"
                      title="Delete branch"
                    >
                      Delete
                    </button>
                  {/if}
                </div>
              </div>
            </div>
          {/each}
        </div>
      </div>
    {/if}
  </div>
</div>

<!-- Create Branch Modal -->
{#if showCreateModal}
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
      <h2 class="text-xl font-bold mb-4">Create New Branch</h2>

      {#if createError}
        <div class="mb-4 p-3 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded">
          {createError}
        </div>
      {/if}

      <div class="mb-4">
        <label class="block text-sm font-medium mb-2">Branch Name</label>
        <input
          type="text"
          bind:value={newBranchName}
          placeholder="feature/new-feature"
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <div class="mb-6">
        <label class="block text-sm font-medium mb-2">Source Branch</label>
        <select
          bind:value={sourceBranch}
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          {#each branches as branch}
            <option value={branch.name}>{branch.name}</option>
          {/each}
        </select>
      </div>

      <div class="flex gap-3">
        <button
          onclick={() => showCreateModal = false}
          class="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg transition-colors"
          disabled={creating}
        >
          Cancel
        </button>
        <button
          onclick={createBranch}
          class="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
          disabled={creating}
        >
          {creating ? 'Creating...' : 'Create Branch'}
        </button>
      </div>
    </div>
  </div>
{/if}

<!-- Delete Confirmation Modal -->
{#if branchToDelete}
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
      <h2 class="text-xl font-bold mb-4 text-red-600 dark:text-red-400">Delete Branch</h2>

      <p class="mb-6">
        Are you sure you want to delete the branch <strong>{branchToDelete}</strong>? This action cannot be undone.
      </p>

      <div class="flex gap-3">
        <button
          onclick={() => branchToDelete = null}
          class="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg transition-colors"
          disabled={deleting}
        >
          Cancel
        </button>
        <button
          onclick={() => branchToDelete && deleteBranch(branchToDelete)}
          class="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
          disabled={deleting}
        >
          {deleting ? 'Deleting...' : 'Delete'}
        </button>
      </div>
    </div>
  </div>
{/if}
